import { DOCUMENT } from "@angular/common";
import { Injectable, effect, inject, signal } from "@angular/core";

export type Theme = 'light' | 'dark';

@Injectable({providedIn: 'root'})
export class ThemeManagerService {
    private readonly THEME_KEY = 'user_theme_preference';
    theme = signal<Theme>(this.getInitialTheme());
    
    private _document = inject(DOCUMENT);

    constructor() {
        // Aplicar el tema inicial
        this.applyTheme(this.theme());
        
        // Efecto reactivo para cambios de tema
        effect(() => {
            const currentTheme = this.theme();
            this.applyTheme(currentTheme);
            this.persistTheme(currentTheme);
        });
    }

    // Método público para cambiar tema
    setTheme(theme: Theme): void {
        this.theme.set(theme);
    }

    toggleTheme(): void {
        this.theme.update(value => value === 'light' ? 'dark' : 'light');
    }

    private getInitialTheme(): Theme {
        // 1. Verificar localStorage
        const savedTheme = localStorage.getItem(this.THEME_KEY);
        if (savedTheme === 'light' || savedTheme === 'dark') {
            return savedTheme as Theme;
        }
        
        // 2. Verificar preferencia del sistema
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        
        // 3. Default
        return 'light';
    }

    private applyTheme(theme: Theme): void {
        if (theme === 'dark') {
            this._document.documentElement.classList.add('dark');
            this._document.documentElement.classList.remove('light');
        } else {
            this._document.documentElement.classList.add('light');
            this._document.documentElement.classList.remove('dark');
        }
    }

    private persistTheme(theme: Theme): void {
        localStorage.setItem(this.THEME_KEY, theme);
    }
}